import React, {useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {Icon} from '@forecast-it/design-system';
import {ICON_DEFAULT, TEXT_CONTENT_MAIN, ERROR_COLOR_2} from './colors';
import type {AttachedFile, SowExtractionStep} from './types';
import {SOW_CHECKLIST_LABELS} from './constants';
import {
	ExtractionBody,
	ExtractionCard,
	CardDivider,
	FileCard,
	FileCardMeta,
	FileCardName,
	FileCardSize,
	FileCardReplace,
	ExtractionStatus,
	SpinnerIcon,
	ChecklistContainer,
	ChecklistRow,
	ChecklistDot,
	ChecklistRadioDot,
	ChecklistLabel,
	RejectionContainer,
	RejectionIconWrap,
	RejectionTitle,
	RejectionMessage,
	DropZoneCard,
	DropZoneContent,
	DropZoneTitleRow,
	DropZoneTitle,
	DropZoneSub,
	MidEmphasisButton,
} from './styles';

export interface SowExtractionProgressProps {
	file: AttachedFile | null; // null = no file uploaded yet (D11: dashed upload zone)
	steps: SowExtractionStep[];
	// True until D12's "Read" transition. Gates the progress checklist: once false, the
	// checklist disappears and the file-row button switches from "Cancel" to "Upload New SoW".
	isReading: boolean;
	// Drives the dashed drop-zone / Browse button when no file has been uploaded yet.
	onUpload: (file: AttachedFile) => void;
	// Swaps in a different file. Only reachable once reading has finished (isReading===false),
	// via the "Upload New SoW" button — a distinct action from onCancel below.
	onReplace: (file: AttachedFile) => void;
	// Aborts the in-flight generation while Nova is still reading (Figma frame 2 shows a single
	// "Cancel" button in this state, not "Replace"). Optional: wiring the real abort mechanism
	// (abortRef.current?.abort()) into this prop is Task 8's job, not this component's.
	onCancel?: () => void;
	// Set when the agent has rejected the document (e.g. classified as an MSA/retainer,
	// not a SoW). Renders in place of the checklist; the file card (with its action button)
	// stays visible so the user can try a different file without leaving this screen.
	rejectionMessage?: string | null;
	// TNSR-532's global gen-ai kill switch. Only meaningful in the `!file` (drop zone) state —
	// mirrors the retired ProjectCreationChoiceModal's own scoping, which never touched an
	// already-in-progress read. The zone stays visible, greyed and inert, with an explanation,
	// rather than disappearing: a switched-off capability should be seen, not just absent.
	disabled?: boolean;
}

const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;
const ACCEPTED_EXTENSIONS = ['.pdf', '.docx', '.md'];

function formatBytes(base64: string): string {
	const bytes = Math.round((base64.length * 3) / 4);
	if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const SowExtractionProgress: React.FC<SowExtractionProgressProps> = ({
	file,
	steps,
	isReading,
	onUpload,
	onReplace,
	onCancel,
	rejectionMessage,
	disabled = false,
}) => {
	const {formatMessage} = useIntl();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const uploadInputRef = useRef<HTMLInputElement>(null);
	const browseRef = useRef<HTMLButtonElement>(null);
	const [replaceError, setReplaceError] = useState<string | null>(null);

	// The dialog auto-focuses its first tabbable element on open, which is Browse, and Chrome
	// treats that programmatic focus as :focus-visible, so the button would open with its keyboard
	// ring drawn. The design-system Modal
	// exposes no onOpenAutoFocus, so the first focus that lands here before the user has pressed a
	// key or a pointer is handed to the dialog container instead (what Radix itself does when nothing
	// is tabbable). Any focus after a real interaction, and any later focus, is left alone.
	const hasFile = !!file;
	useEffect(() => {
		const browse = browseRef.current;
		if (hasFile || !browse) return;
		let interacted = false;
		const markInteracted = () => {
			interacted = true;
		};
		const redirectAutoFocus = () => {
			browse.removeEventListener('focus', redirectAutoFocus);
			if (interacted) return;
			(browse.closest('[role="dialog"]') as HTMLElement | null)?.focus();
		};
		browse.addEventListener('focus', redirectAutoFocus);
		window.addEventListener('keydown', markInteracted, true);
		window.addEventListener('pointerdown', markInteracted, true);
		if (document.activeElement === browse) redirectAutoFocus();
		return () => {
			browse.removeEventListener('focus', redirectAutoFocus);
			window.removeEventListener('keydown', markInteracted, true);
			window.removeEventListener('pointerdown', markInteracted, true);
		};
	}, [hasFile]);
	const [uploadError, setUploadError] = useState<string | null>(null);
	const [isDragging, setIsDragging] = useState(false);

	const handleReplaceFile = (e: React.ChangeEvent<HTMLInputElement>) => {
		const f = e.target.files?.[0];
		if (!f) return;
		setReplaceError(null);
		if (f.size > 20 * 1024 * 1024) {
			setReplaceError(formatMessage({id: 'sow_upload.file_too_large'}));
			if (fileInputRef.current) fileInputRef.current.value = '';
			return;
		}
		const reader = new FileReader();
		reader.onload = evt => {
			const b64 = (evt.target?.result as string).split(',')[1] ?? '';
			if (fileInputRef.current) fileInputRef.current.value = '';
			onReplace({filename: f.name, mimeType: f.type, dataBase64: b64});
		};
		reader.onerror = () => {
			setReplaceError(formatMessage({id: 'sow_upload.file_read_error'}));
			if (fileInputRef.current) fileInputRef.current.value = '';
		};
		reader.readAsDataURL(f);
	};

	// Independent from handleReplaceFile above (some duplication accepted): this path also
	// validates the file extension, since drag-and-drop bypasses the file input's `accept`
	// filter in a way a file-picker selection does not.
	const handleUploadFile = (f: File) => {
		setUploadError(null);
		const ext = '.' + (f.name.split('.').pop() ?? '').toLowerCase();
		if (!ACCEPTED_EXTENSIONS.includes(ext)) {
			setUploadError(formatMessage({id: 'sow_upload.unsupported_format'}));
			if (uploadInputRef.current) uploadInputRef.current.value = '';
			return;
		}
		if (f.size > MAX_FILE_SIZE_BYTES) {
			setUploadError(formatMessage({id: 'sow_upload.file_too_large'}));
			if (uploadInputRef.current) uploadInputRef.current.value = '';
			return;
		}
		const reader = new FileReader();
		reader.onload = evt => {
			const b64 = (evt.target?.result as string).split(',')[1] ?? '';
			if (uploadInputRef.current) uploadInputRef.current.value = '';
			onUpload({filename: f.name, mimeType: f.type, dataBase64: b64});
		};
		reader.onerror = () => {
			setUploadError(formatMessage({id: 'sow_upload.file_read_error'}));
			if (uploadInputRef.current) uploadInputRef.current.value = '';
		};
		reader.readAsDataURL(f);
	};

	const handleUploadInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const f = e.target.files?.[0];
		if (f) handleUploadFile(f);
	};

	const handleUploadDrop = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		setIsDragging(false);
		const f = e.dataTransfer.files?.[0];
		if (f) handleUploadFile(f);
	};

	if (!file) {
		return (
			<ExtractionBody>
				<DropZoneCard
					isDragging={!disabled && isDragging}
					isDisabled={disabled}
					aria-disabled={disabled}
					onDragOver={e => {
						e.preventDefault();
						if (!disabled) setIsDragging(true);
					}}
					onDragLeave={() => setIsDragging(false)}
					onDrop={e => {
						// Swallowed rather than ignored: without preventDefault the browser navigates
						// away to the dropped file.
						e.preventDefault();
						if (!disabled) handleUploadDrop(e);
					}}
					onClick={() => {
						if (!disabled) uploadInputRef.current?.click();
					}}
					data-cy="sow-extraction-drop-zone"
				>
					<DropZoneContent>
						<DropZoneTitleRow>
							{/* Sparkle icon, not upload, as the design specifies. */}
							<Icon icon="sparkle" size="m" customColor={ICON_DEFAULT} />
							<DropZoneTitle>{formatMessage({id: 'sow_upload.zone_title'})}</DropZoneTitle>
						</DropZoneTitleRow>
						<DropZoneSub>{formatMessage({id: 'sow_upload.zone_description'})}</DropZoneSub>
						<DropZoneSub>{formatMessage({id: 'sow_upload.zone_formats'})}</DropZoneSub>
						{disabled && (
							<DropZoneSub style={{color: ERROR_COLOR_2}} data-cy="sow-upload-zone-ai-disabled">
								{formatMessage({id: 'gen_ai.disabled_label'})}
							</DropZoneSub>
						)}
						{uploadError && (
							<div style={{fontSize: 12, color: ERROR_COLOR_2, padding: '2px 0'}} data-cy="sow-upload-zone-error">
								{uploadError}
							</div>
						)}
					</DropZoneContent>
					<MidEmphasisButton
						ref={browseRef}
						type="button"
						disabled={disabled}
						onClick={e => {
							e.stopPropagation();
							if (!disabled) uploadInputRef.current?.click();
						}}
					>
						{formatMessage({id: 'sow_upload.browse'})}
					</MidEmphasisButton>
				</DropZoneCard>
				<input
					ref={uploadInputRef}
					type="file"
					accept=".pdf,.docx,.md"
					style={{display: 'none'}}
					disabled={disabled}
					onChange={handleUploadInputChange}
					data-cy="sow-upload-zone-input"
				/>
			</ExtractionBody>
		);
	}

	const activeStep = steps.find(s => s.status === 'active');
	const allDone = steps.length > 0 && steps.every(s => s.status === 'done');

	return (
		<ExtractionBody>
			<ExtractionCard>
				<FileCard>
					<FileCardMeta>
						<FileCardName title={file.filename}>{file.filename}</FileCardName>
						<FileCardSize>
							{formatBytes(file.dataBase64)} · {formatMessage({id: 'sow_upload.uploaded_just_now'})}
						</FileCardSize>
					</FileCardMeta>
					<FileCardReplace
						onClick={() => {
							// While reading, this is a Cancel action (aborts the in-flight generation) —
							// a distinct action from Replace, and Figma frame 2 shows only one button
							// here, not both. Once reading finishes, it becomes Upload New SoW/onReplace.
							if (isReading) {
								onCancel?.();
								return;
							}
							setReplaceError(null);
							fileInputRef.current?.click();
						}}
					>
						{formatMessage({id: isReading ? 'common.cancel' : 'sow_upload.upload_new_sow'})}
					</FileCardReplace>
					<input
						ref={fileInputRef}
						type="file"
						accept=".pdf,.docx,.md"
						style={{display: 'none'}}
						onChange={handleReplaceFile}
					/>
				</FileCard>
				{replaceError && <div style={{fontSize: 12, color: ERROR_COLOR_2, padding: '2px 0'}}>{replaceError}</div>}
				{(rejectionMessage || isReading) && <CardDivider />}

				{rejectionMessage ? (
					<RejectionContainer>
						<RejectionIconWrap>
							<Icon icon="warning" size="m" customColor={ERROR_COLOR_2} />
						</RejectionIconWrap>
						<RejectionTitle>{formatMessage({id: 'sow_upload.rejected_title'})}</RejectionTitle>
						<RejectionMessage>{rejectionMessage}</RejectionMessage>
					</RejectionContainer>
				) : isReading ? (
					<>
						<ExtractionStatus>
							<SpinnerIcon>
								<Icon icon="reload" size="s" customColor={TEXT_CONTENT_MAIN} />
							</SpinnerIcon>
							<Icon icon="sparkle" size="s" customColor={ICON_DEFAULT} />
							{allDone
								? formatMessage({id: 'sow_upload.nova_finalising'})
								: activeStep
								? `${formatMessage({id: 'sow_upload.nova_reading'})} ${
										SOW_CHECKLIST_LABELS[activeStep.label] ?? activeStep.label
								  }…`
								: formatMessage({id: 'sow_upload.nova_reading_sow'})}
						</ExtractionStatus>

						<ChecklistContainer>
							{steps.map(step => (
								<ChecklistRow key={step.label}>
									<ChecklistDot status={step.status}>
										{step.status === 'done' && <ChecklistRadioDot data-cy="checklist-radio-dot" />}
									</ChecklistDot>
									<ChecklistLabel status={step.status}>
										{SOW_CHECKLIST_LABELS[step.label] ?? step.label}
									</ChecklistLabel>
								</ChecklistRow>
							))}
						</ChecklistContainer>
					</>
				) : null}
			</ExtractionCard>
		</ExtractionBody>
	);
};

export default SowExtractionProgress;
